import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIRS8, cameraAvoidPhrases, type Cam, type Direction8 } from '@il-colpo/engine';
import { Button } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { useLooseT } from '../text/loose.ts';
import { Bystander, ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface Cell {
  x: number;
  y: number;
}
interface CamsView {
  size: number;
  cams?: {
    id: number;
    x: number;
    y: number;
    range: number;
    orientation: Direction8;
    cone: Cell[];
  }[];
  camPositions?: Cam[];
  failures?: number;
  lockedUntilMs?: number;
  clues: { camPath?: Cell[] };
}

const CELL = 44;
const HEARD = ['camAvoid', 'camNow'] as const;
const ARROW: Record<Direction8, string> = {
  n: '↑',
  ne: '↗',
  e: '→',
  se: '↘',
  s: '↓',
  sw: '↙',
  w: '←',
  nw: '↖',
};

/** Cameras (GDD §8.4): turn every camera away from the guards' path, then check. */
export function CamerasPanel({ puzzleId, type, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act, view } = useGame();
  const dirName = useLooseT('phrases');
  const v = raw as unknown as CamsView;
  const [selected, setSelected] = useState(0);

  const grid = v.size * CELL;
  const path = v.clues.camPath;

  if (role === 'hacker' && v.cams) {
    const cam = v.cams[selected] ?? v.cams[0];
    const locked = (v.lockedUntilMs ?? 0) > (view.exec?.elapsedMs ?? 0);
    return (
      <div className={styles['stack']}>
        <svg
          viewBox={`0 0 ${grid} ${grid}`}
          className={styles['board']}
          role="group"
          aria-label={t('puzzle.cameras.board')}
        >
          <Grid size={v.size} />
          {v.cams.flatMap((c) =>
            c.cone.map((cell) => (
              <rect
                key={`${c.id}-${cell.x}-${cell.y}`}
                className={styles['cone']}
                x={cell.x * CELL + 2}
                y={cell.y * CELL + 2}
                width={CELL - 4}
                height={CELL - 4}
              />
            )),
          )}
          {v.cams.map((c, i) => (
            <g
              key={c.id}
              role="button"
              tabIndex={0}
              aria-label={t('puzzle.cameras.camera', {
                n: c.id + 1,
                dir: dirName(`enum.${c.orientation}`),
              })}
              className={[styles['camera'], i === selected ? styles['selected'] : ''].join(' ')}
              transform={`translate(${c.x * CELL + CELL / 2},${c.y * CELL + CELL / 2})`}
              onClick={() => setSelected(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelected(i);
                }
              }}
            >
              <circle r={15} />
              <text textAnchor="middle" y={5}>
                {`${c.id + 1}${ARROW[c.orientation]}`}
              </text>
            </g>
          ))}
        </svg>
        {cam ? (
          <div
            className={styles['compass']}
            role="group"
            aria-label={t('puzzle.cameras.turn', { n: cam.id + 1 })}
          >
            {DIRS8.map((d) => (
              <Button
                key={d}
                size="md"
                variant="secondary"
                selected={cam.orientation === d}
                aria-label={dirName(`enum.${d}`)}
                onClick={() =>
                  act(puzzleId, { kind: 'cameras.rotate', camera: cam.id, orientation: d })
                }
              >
                {ARROW[d]}
              </Button>
            ))}
          </div>
        ) : null}
        <Button disabled={locked} onClick={() => act(puzzleId, { kind: 'cameras.verify' })}>
          {locked ? t('puzzle.cameras.locked') : t('puzzle.cameras.verify')}
        </Button>
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }

  if (path && v.camPositions) {
    const avoid = cameraAvoidPhrases(v.camPositions, path);
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.cameras.pathHint')}</p>
        <svg
          viewBox={`0 0 ${grid} ${grid}`}
          className={styles['board']}
          role="img"
          aria-label={t('puzzle.cameras.pathBoard')}
        >
          <Grid size={v.size} />
          {path.map((c, i) => (
            <g key={i} transform={`translate(${c.x * CELL},${c.y * CELL})`}>
              <rect className={styles['path']} x={3} y={3} width={CELL - 6} height={CELL - 6} />
              <text
                x={CELL / 2}
                y={CELL / 2 + 5}
                textAnchor="middle"
                className={styles['pathText']}
              >
                {i + 1}
              </text>
            </g>
          ))}
          {v.camPositions.map((c) => (
            <g
              key={c.id}
              transform={`translate(${c.x * CELL + CELL / 2},${c.y * CELL + CELL / 2})`}
              className={styles['camera']}
            >
              <circle r={15} />
              <text textAnchor="middle" y={5}>
                {c.id + 1}
              </text>
            </g>
          ))}
        </svg>
        <ul className={styles['clues']}>
          {avoid.map((a) => (
            <ClueLine key={`${a.camera}-${a.dir}`} phrase={a.phrase} />
          ))}
        </ul>
      </div>
    );
  }
  return <Bystander type={type} />;
}

function Grid({ size }: { size: number }) {
  return (
    <>
      {Array.from({ length: size * size }, (_, i) => (
        <rect
          key={i}
          className={styles['cell']}
          x={(i % size) * CELL}
          y={Math.floor(i / size) * CELL}
          width={CELL}
          height={CELL}
        />
      ))}
    </>
  );
}
